import React, { useCallback, useEffect, useMemo, useState } from "react";
import { postSupportAiChat, postSupportAiEscalate, getSupportAiVerificationSummary } from "../../services/supportApi";

const PROMPT_CHIPS = [
  {
    label: "Wallet not syncing",
    text: "My wallet is connected but my NFT or purchase does not show on the Treydora dashboard. What should I check?",
  },
  {
    label: "Purchase completed but access missing",
    text: "I completed a purchase but I still do not see access in My Library or the product I bought. What are the next steps?",
  },
  {
    label: "My Library item missing",
    text: "An item I believe I own is missing from My Library. How should I troubleshoot before contacting staff?",
  },
  {
    label: "Storefront locked",
    text: "My Storefront or storefront tools appear locked even though I think I meet the requirements. What should I verify?",
  },
  {
    label: "Report a bug",
    text: "I want to report a bug: something breaks on a specific page. How should I document it for support?",
  },
];

/**
 * Treydora.com dashboard — AI support oracle (server-backed; no API keys client-side).
 */
export default function SupportAiAssistantPanel({ onTicketCreated }) {
  const [thread, setThread] = useState([]);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [lastAi, setLastAi] = useState(null);
  const [escalating, setEscalating] = useState(false);
  const [escalateMsg, setEscalateMsg] = useState("");
  const [baselineVerifyChips, setBaselineVerifyChips] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await getSupportAiVerificationSummary();
        const sig = data?.signals;
        if (!sig || cancelled) return;
        const chips = [];
        if (sig.hasLibraryEntitlements) chips.push(`Library access: found (${sig.libraryEntitlementCount || 0})`);
        else chips.push("Library access: not found");
        if (sig.hasGenesisComicAccess === true) chips.push("Genesis comic: on record");
        else if (sig.hasGenesisComicAccess === false) chips.push("Genesis comic: not on record");
        else chips.push("Genesis comic: unknown");
        if (sig.hasVrRealEstateOwnership === true) chips.push("VR real estate: on record");
        else if (sig.hasVrRealEstateOwnership === false) chips.push("VR real estate: not on record");
        else chips.push("VR real estate: unknown");
        if (sig.hasStorefrontEligibility === true) chips.push("Storefront: eligible");
        else if (sig.hasStorefrontEligibility === false) chips.push("Storefront: not eligible");
        else chips.push("Storefront: unknown");
        if (sig.recentOpenSupportTicketCount > 0) chips.push("Recent tickets: yes");
        else chips.push("Recent open tickets: none (30d)");
        setBaselineVerifyChips(chips.slice(0, 8));
      } catch {
        if (!cancelled) setBaselineVerifyChips([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const userIssueText = useMemo(() => {
    const parts = thread.filter((m) => m.role === "user").map((m) => m.content);
    return parts.join("\n\n").trim().slice(0, 5000);
  }, [thread]);

  const verifyStripChips = useMemo(() => {
    if (Array.isArray(lastAi?.verificationChips) && lastAi.verificationChips.length) return lastAi.verificationChips;
    return baselineVerifyChips;
  }, [lastAi, baselineVerifyChips]);

  const sendChip = useCallback((text) => {
    setDraft(text);
  }, []);

  const sendMessage = async () => {
    const text = draft.trim();
    if (!text || busy) return;
    const prev = thread;
    const nextThread = [...prev, { role: "user", content: text }];
    setThread(nextThread);
    setDraft("");
    setError("");
    setEscalateMsg("");
    setBusy(true);
    try {
      const data = await postSupportAiChat({
        messages: nextThread.map(({ role, content }) => ({ role, content })),
        context: { currentPage: "/dashboard/support" },
      });
      setLastAi(data);
      const assistantMsg =
        typeof data?.assistantMessage === "string" && data.assistantMessage.trim()
          ? data.assistantMessage.trim()
          : "I could not generate a reply. Please try again or use the standard ticket form.";
      setThread([...nextThread, { role: "assistant", content: assistantMsg }]);
      if (data?.aiConfigured === false) {
        setEscalateMsg("AI support is unavailable right now. You can still open a standard support ticket below.");
      }
    } catch (e) {
      setThread(prev);
      setDraft(text);
      setError(e?.message || "Could not reach the support assistant.");
      setEscalateMsg("AI support is unavailable right now. You can still open a standard support ticket below.");
    } finally {
      setBusy(false);
    }
  };

  const canEscalate = thread.length > 0 && lastAi;

  const doEscalate = async () => {
    if (!canEscalate || escalating) return;
    const issue = userIssueText;
    if (issue.length < 10) {
      setError(
        "Add a bit more detail in the chat before creating a ticket (at least 10 characters across your messages)."
      );
      return;
    }
    if (!lastAi) return;
    setEscalating(true);
    setError("");
    setEscalateMsg("");
    try {
      const payload = {
        conversationMessages: thread.map(({ role, content }) => ({ role, content })),
        userIssue: issue,
        suggestedCategory: lastAi.suggestedCategory || "Other",
        suggestedPriority: lastAi.suggestedPriority || "Normal",
        suggestedTicketSubject: lastAi.suggestedTicketSubject || "Support request (AI assistant)",
        suggestedTicketDescription: lastAi.suggestedTicketDescription || "",
        aiSummary: [lastAi.escalationReason, lastAi.suggestedTicketDescription, lastAi.assistantMessage]
          .filter(Boolean)
          .join("\n\n")
          .slice(0, 4000),
        aiEscalationReason: lastAi.escalationReason || "",
      };
      const created = await postSupportAiEscalate(payload);
      setEscalateMsg(`Official ticket ${created?.ticketCode || ""} created. Staff can review it in TreydoraED.`);
      setThread([]);
      setLastAi(null);
      if (typeof onTicketCreated === "function") {
        onTicketCreated(created);
      }
    } catch (e) {
      setError(e?.message || "Could not create ticket from this conversation.");
    } finally {
      setEscalating(false);
    }
  };

  const scrollToManualForm = () => {
    const el = document.getElementById("dsc-ticket-title");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section className="dsc-panel dsc-panel--ai dg-gold-ring" aria-labelledby="dsc-ai-title">
      <div className="dsc-aiHead">
        <div>
          <h2 id="dsc-ai-title" className="dsc-panel__title serif">
            Treydora Support Assistant
          </h2>
          <p className="dsc-panel__lead dsc-panel__lead--tight">
            Ask about wallet sync, purchases, NFT access, My Library, reader access, VR real estate, or storefront issues.
            If the assistant cannot resolve it, it can prepare an official ticket for admin review.
          </p>
        </div>
      </div>

      <div className="dsc-aiChips" aria-label="Quick prompts">
        {PROMPT_CHIPS.map((c) => (
          <button
            key={c.label}
            type="button"
            className="dsc-aiChip"
            onClick={() => sendChip(c.text)}
            disabled={busy}
          >
            {c.label}
          </button>
        ))}
      </div>

      <div className="dsc-aiThread" role="log" aria-live="polite" aria-relevant="additions">
        {thread.length === 0 ? (
          <p className="dsc-aiEmpty dg-muted">
            Describe your issue below. The assistant answers from TreydoraEdBackend — never share seed phrases or private
            keys.
          </p>
        ) : null}
        {thread.map((m, idx) => (
          <div key={`${idx}-${m.role}`} className={`dsc-aiMsg dsc-aiMsg--${m.role}`}>
            <span className="dsc-aiMsg__label">{m.role === "user" ? "You" : "Support Assistant"}</span>
            <p className="dsc-aiMsg__body">{m.content}</p>
          </div>
        ))}
        {busy ? (
          <p className="dsc-aiThinking dg-muted" role="status">
            Consulting support guidance…
          </p>
        ) : null}
      </div>

      {verifyStripChips.length > 0 && thread.length > 0 ? (
        <div className="dsc-aiVerifyStrip" aria-label="Account signals from dashboard records (read-only)">
          <span className="dsc-aiVerifyStrip__label">Account signals</span>
          {verifyStripChips.map((c, i) => (
            <span key={`${c}-${i}`} className="dsc-aiVerifyChip">
              {c}
            </span>
          ))}
        </div>
      ) : null}

      {lastAi && !busy ? (
        <div className="dsc-aiMeta">
          {lastAi.suggestedCategory ? (
            <span className="dsc-aiPill">
              Category: <strong>{lastAi.suggestedCategory}</strong>
            </span>
          ) : null}
          {lastAi.suggestedPriority ? (
            <span className="dsc-aiPill">
              Priority: <strong>{lastAi.suggestedPriority}</strong>
            </span>
          ) : null}
        </div>
      ) : null}

      {lastAi?.safeActionHints && Array.isArray(lastAi.safeActionHints) && lastAi.safeActionHints.length > 0 ? (
        <ul className="dsc-aiHints">
          {lastAi.safeActionHints.map((h, i) => (
            <li key={i}>{h}</li>
          ))}
        </ul>
      ) : null}

      {lastAi?.shouldEscalate ? (
        <div className="dsc-aiEscalate" role="region" aria-label="Escalation suggestion">
          <h3 className="dsc-aiEscalate__title">This may need admin review</h3>
          {lastAi.escalationReason ? <p className="dsc-aiEscalate__why">{lastAi.escalationReason}</p> : null}
          {lastAi.suggestedTicketSubject ? (
            <p className="dsc-aiEscalate__subj">
              <span className="dg-muted">Suggested subject:</span> {lastAi.suggestedTicketSubject}
            </p>
          ) : null}
          <div className="dsc-aiEscalate__actions">
            <button type="button" className="dg-btn dg-btn--primary" disabled={escalating} onClick={() => void doEscalate()}>
              {escalating ? "Creating ticket…" : "Create ticket from this conversation"}
            </button>
          </div>
        </div>
      ) : null}

      {!lastAi?.shouldEscalate && thread.length >= 2 ? (
        <div className="dsc-aiEscalate dsc-aiEscalate--soft">
          <p className="dsc-aiEscalate__why mb-0">
            Want staff to review this thread? You can still open an official ticket with the same conversation.
          </p>
          <div className="dsc-aiEscalate__actions">
            <button type="button" className="dg-btn dg-btn--gold-outline" disabled={escalating} onClick={() => void doEscalate()}>
              {escalating ? "Creating ticket…" : "Create ticket from this conversation"}
            </button>
          </div>
        </div>
      ) : null}

      <div className="dsc-aiComposer">
        <label className="dsc-label" htmlFor="dsc-ai-input">
          Message
        </label>
        <textarea
          id="dsc-ai-input"
          className="dsc-textarea dsc-textarea--ai"
          rows={3}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Example: I connected MetaMask but my Genesis comic still does not unlock…"
          disabled={busy}
        />
        <div className="dsc-aiComposer__row">
          <button type="button" className="dg-btn dg-btn--gold-outline" disabled={busy || !draft.trim()} onClick={() => void sendMessage()}>
            {busy ? "Sending…" : "Send to assistant"}
          </button>
          <button type="button" className="dg-btn dg-btn--ghost" onClick={scrollToManualForm}>
            Open standard ticket form
          </button>
        </div>
      </div>

      {error ? (
        <p className="dsc-errorBanner dsc-errorBanner--tight" role="alert">
          {error}
        </p>
      ) : null}
      {escalateMsg ? (
        <p className="dsc-successBanner dsc-successBanner--tight" role="status">
          {escalateMsg}
        </p>
      ) : null}
    </section>
  );
}
